import { z } from 'zod';
import { messageKey } from './messages.js';
/**
 * Производители камер (§7.7). Влияет только на архив (фаза 8) и на подсказки
 * путей RTSP при заведении: живой поток берётся по указанному пути, а не
 * по угаданному, — платформа ничего на камере не перенастраивает (§1.3.1).
 */
export const CAMERA_VENDORS = ['generic', 'hikvision', 'dahua', 'axis', 'onvif'];
export const cameraVendorSchema = z.enum(CAMERA_VENDORS);
/**
 * Транспорт RTSP. `tcp` по умолчанию: через туннель агента (§9) идёт
 * TCP-поток, и interleaved-режим — единственный, который в него укладывается.
 * `udp` остаётся для камер в одной сети с платформой.
 */
export const CAMERA_TRANSPORTS = ['tcp', 'udp'];
export const cameraTransportSchema = z.enum(CAMERA_TRANSPORTS);
/**
 * Кодек, обнаруженный зондированием.
 *
 * `unknown` — зондирование прошло, но кодек не опознан; `null` в поле
 * камеры — не зондировали вовсе. Разница важна интерфейсу: во втором случае
 * предлагать «включите транскодирование» не за что.
 */
export const CODEC_HINTS = ['h264', 'h265', 'unknown'];
export const codecHintSchema = z.enum(CODEC_HINTS);
/** Кодеки, которые браузер декодирует сам, без транскодирования на сервере. */
export const BROWSER_SUPPORTED_CODECS = ['h264'];
/** Провайдеры архива (§11, фаза 8). Здесь — только чтобы хранить выбор. */
export const ARCHIVE_PROVIDERS = ['rtsp_replay', 'hikvision_isapi', 'dahua_cgi'];
export const archiveProviderSchema = z.enum(ARCHIVE_PROVIDERS);
/** Какой поток запрашивает зритель: основной или substream (§11, фаза 7, задача 7). */
export const STREAM_QUALITIES = ['main', 'sub'];
export const streamQualitySchema = z.enum(STREAM_QUALITIES);
/**
 * Транспорт доставки в браузер. WebRTC — основной, MSE поверх WebSocket —
 * резервный на случай, когда WebRTC заблокирован файрволом (задача 8).
 */
export const STREAM_TRANSPORTS = ['webrtc', 'mse'];
export const streamTransportSchema = z.enum(STREAM_TRANSPORTS);
/**
 * Сколько ждать установления WebRTC, прежде чем перейти на MSE (задача 8).
 *
 * Пять секунд задано ТЗ. Меньше нельзя: сбор ICE-кандидатов на медленной
 * сети занимает секунды и без всякой блокировки.
 */
export const WEBRTC_FALLBACK_TIMEOUT_MS = 5_000;
/** Одновременных потоков на клиента (задача 11). */
export const MAX_STREAMS_PER_CLIENT = 16;
/** Тикет на поток живёт столько же, сколько тикет VNC-сессии (§8.2). */
export const STREAM_TICKET_TTL_SEC = 30;
/**
 * Аренда зрителя: столько живёт запись о том, что камеру кто-то смотрит.
 *
 * WHEP — это один POST с обменом SDP, а не долгое соединение, и по нему
 * нельзя понять, ушёл зритель или сидит. Поэтому браузер продлевает аренду
 * (`VIEWER_KEEPALIVE_INTERVAL_MS`), а истёкшая означает ушедшего зрителя.
 */
export const VIEWER_LEASE_TTL_SEC = 30;
/** Как часто браузер продлевает аренду. Втрое чаще срока — с запасом на потери. */
export const VIEWER_KEEPALIVE_INTERVAL_MS = 10_000;
/**
 * Через сколько после ухода последнего зрителя поток гасится в go2rtc
 * (задача 6). Тридцать секунд задано ТЗ: переключение раскладки не должно
 * приводить к переподключению к камере.
 */
export const STREAM_IDLE_SHUTDOWN_MS = 30_000;
/**
 * Имя потока в go2rtc для конкретного качества.
 *
 * В БД хранится база (`go2rtc_name`), а потоков у камеры два: основной
 * и substream. Одним потоком с двумя источниками их сделать нельзя —
 * go2rtc выбирает источник сам, а раскладке 3×3 нужен именно substream
 * и никакой другой (задача 7).
 */
export function go2rtcStreamName(base, quality) {
    return `${base}_${quality}`;
}
/** Путь RTSP: `/Streaming/Channels/101`, `/cam/realmonitor?channel=1&subtype=0`. */
const rtspPathSchema = z
    .string()
    .trim()
    .min(1, messageKey('validation.camera.streamPathRequired'))
    .max(512)
    .refine((value) => value.startsWith('/'), messageKey('validation.camera.streamPathSlash'))
    // Перевод строки в пути — это подстановка чужой строки в URL, который
    // уйдёт в go2rtc. Отсекаем на границе, а не при сборке URL. Решётка
    // отделяет там параметры источника (`#transport=udp`), поэтому в пути
    // она не просто лишний символ, а подмена наших настроек чужими.
    .refine((value) => !/[\s\\#]/.test(value), messageKey('validation.camera.streamPathChars'));
export const cameraSchema = z.object({
    id: z.uuid(),
    nodeId: z.uuid(),
    /** Имя узла и площадка — чтобы список камер не требовал второго запроса. */
    nodeName: z.string(),
    nodeAddress: z.string().nullable(),
    /**
     * Заметки узла — то, что инженер написал про эту камеру своими словами:
     * «смотрит на ворота», «вход со двора, свет только днём».
     *
     * Приходит вместе с камерой по той же причине, что имя и площадка: на стене
     * шестнадцать ячеек, и второй запрос за описанием к каждой означал бы
     * шестнадцать запросов ради подсказки под курсором.
     */
    nodeNotes: z.string().nullable(),
    siteId: z.uuid(),
    siteName: z.string(),
    /**
     * Номер канала регистратора (ADR 037). `null` — самостоятельная камера.
     *
     * По нему стена сворачивает каналы одного узла в одну ячейку с выбором
     * канала, а маршруты камеры отказываются править строку, которую
     * заводило определение каналов.
     */
    channel: z.number().int().nullable(),
    /** Имя канала в самом регистраторе: «Въезд», «Склад 2». */
    channelName: z.string().nullable(),
    vendor: cameraVendorSchema,
    mainPath: z.string(),
    subPath: z.string().nullable(),
    port: z.number().int(),
    transport: cameraTransportSchema,
    codecHint: codecHintSchema.nullable(),
    archiveProvider: archiveProviderSchema.nullable(),
    archiveConfig: z.record(z.string(), z.unknown()),
    isEnabled: z.boolean(),
    // — Результаты последнего зондирования (задача 3) ————————————
    /** Разрешение основного потока. `null` — не зондировали или не определилось. */
    probedWidth: z.number().int().nullable(),
    probedHeight: z.number().int().nullable(),
    /** Ответил ли substream. `null` — не зондировали; `false` — задан, но молчит. */
    subAvailable: z.boolean().nullable(),
    lastProbeAt: z.iso.datetime().nullable(),
    lastProbeError: z.string().nullable(),
    /** Привязаны ли к узлу учётные данные RTSP. Сам секрет наружу не уходит (§3.3). */
    hasCredential: z.boolean(),
});
const rtspPortSchema = z.coerce.number().int().min(1).max(65535);
const archiveConfigSchema = z.record(z.string(), z.unknown());
export const createCameraSchema = z.object({
    nodeId: z.uuid(),
    vendor: cameraVendorSchema.default('generic'),
    mainPath: rtspPathSchema,
    subPath: rtspPathSchema.nullish(),
    port: rtspPortSchema.default(554),
    transport: cameraTransportSchema.default('tcp'),
    archiveProvider: archiveProviderSchema.nullish(),
    archiveConfig: archiveConfigSchema.default({}),
    isEnabled: z.boolean().default(true),
});
/**
 * Изменение камеры. Поля перечислены заново, а не через `.partial()`
 * от создания: `.partial()` сохраняет умолчания, и `PATCH` с пустым телом
 * сбросил бы порт и транспорт к заводским значениям вместо «ничего не менять».
 *
 * `nodeId` отсутствует намеренно: камера — это свойство узла, а не запись,
 * которую переставляют с узла на узел. Переезд означал бы новую камеру.
 */
export const updateCameraSchema = z.object({
    vendor: cameraVendorSchema.optional(),
    mainPath: rtspPathSchema.optional(),
    subPath: rtspPathSchema.nullish(),
    port: rtspPortSchema.optional(),
    transport: cameraTransportSchema.optional(),
    archiveProvider: archiveProviderSchema.nullish(),
    archiveConfig: archiveConfigSchema.optional(),
    isEnabled: z.boolean().optional(),
});
/**
 * Результат зондирования (задача 3).
 *
 * `reachable: false` — до камеры не достучались; причина в `error` человеческим
 * языком, потому что идти с ней администратору, а не в лог.
 */
export const cameraProbeResultSchema = z.object({
    reachable: z.boolean(),
    codec: codecHintSchema.nullable(),
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    /** Проверялся отдельно: substream может быть задан и не отвечать. */
    subAvailable: z.boolean().nullable(),
    /** Через агента площадки или напрямую — видно на экране камеры. */
    viaAgent: z.string().nullable(),
    error: z.string().nullable(),
    at: z.iso.datetime(),
});
export const createStreamSchema = z.object({
    quality: streamQualitySchema.default('main'),
});
/**
 * Выданный доступ к потоку (§8.2).
 *
 * Адреса относительные: домен у SPA и api один, их разводит обратный прокси.
 * Прямого адреса go2rtc здесь нет и быть не может — снаружи он закрыт
 * (задача 5), и знать о нём браузеру незачем.
 */
export const cameraStreamSchema = z.object({
    /** Куда слать SDP-offer: `POST` с `application/sdp`. */
    whepUrl: z.string(),
    /** Резервный транспорт: WebSocket с MSE-потоком (задача 8). */
    mseUrl: z.string(),
    ticket: z.string(),
    expiresAt: z.iso.datetime(),
    /**
     * Аренда зрителя. Пока её продлевают, поток в go2rtc живёт; после
     * истечения он гасится через `STREAM_IDLE_SHUTDOWN_MS` (задача 6).
     */
    leaseId: z.uuid(),
    /** Сессия §7.8 вида `video_live` — под неё пишется аудит и трафик. */
    sessionId: z.uuid(),
    /**
     * Что выдали на самом деле. Просили `sub`, а его нет — вернём `main`
     * и скажем об этом: §12 запрещает молча подменять (задача 7).
     */
    quality: streamQualitySchema,
    /** Заполнено, когда выданное качество не совпало с запрошенным. */
    qualityWarning: z.string().nullable(),
    codecHint: codecHintSchema.nullable(),
});
/**
 * Тикет на просмотр потока (§11, фаза 7, задача 5).
 *
 * Лежит в Redis, гасится при первом использовании. Одноразовость важнее,
 * чем у тикета VNC: WHEP — обычный POST, и адрес с тикетом в нём легко
 * скопировать из инструментов разработчика и переслать.
 *
 * Ни адреса камеры, ни учётных данных здесь нет — только имя потока внутри
 * go2rtc. Наружу оно ничего не открывает: до самого go2rtc снаружи
 * не достучаться (задача 5).
 */
export const streamTicketSchema = z.object({
    cameraId: z.uuid(),
    orgId: z.uuid(),
    userId: z.uuid(),
    sessionId: z.uuid(),
    leaseId: z.uuid(),
    /** Полное имя потока в go2rtc, уже с суффиксом качества. */
    streamName: z.string(),
});
/** Ключ тикета в Redis. Префикс общий, чтобы их было видно одним `SCAN`. */
export function streamTicketKey(ticket) {
    return `video:ticket:${ticket}`;
}
/**
 * Аренда зрителя: `video:lease:<id>` — кто и что смотрит.
 *
 * Отдельным ключом со своим TTL, а не полем в списке: продление аренды —
 * самая частая операция видеостены, и она должна быть одной командой.
 */
export function viewerLeaseKey(leaseId) {
    return `video:lease:${leaseId}`;
}
/**
 * Зрители одного потока: хеш `leaseId → срок аренды`.
 *
 * Хеш, а не набор ключей с общим префиксом: считать зрителей приходится
 * при каждом обходе на гашение, а `SCAN` по префиксу — это обход всей базы
 * ради числа, которое умещается в одну команду.
 *
 * В имени есть и камера, и качество: раскладка 3×3 и полный экран — два
 * разных потока в go2rtc, и гаснут они порознь.
 */
export function streamViewersKey(streamName) {
    return `video:viewers:${streamName}`;
}
/** Аренды одного пользователя — по ним считается предел §11 задача 11. */
export function userLeasesKey(userId) {
    return `video:user:${userId}`;
}
/**
 * Новый тикет к уже открытой аренде.
 *
 * Нужен переключению транспорта (задача 8): тикет одноразовый, и попытка
 * WebRTC его гасит. Просить ради MSE новый поток означало бы открыть вторую
 * сессию на ту же ячейку и занять второе место в пределе одновременных.
 */
export const streamTicketGrantSchema = z.object({
    ticket: z.string(),
    expiresAt: z.iso.datetime(),
});
/** Ответ на продление аренды: сколько ещё живёт и жив ли поток. */
export const streamKeepaliveSchema = z.object({
    leaseId: z.uuid(),
    expiresAt: z.iso.datetime(),
});
//# sourceMappingURL=cameras.js.map